import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatAxiosError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCapabilities } from "@/context/CapabilitiesContext";
import { useDialog } from "@/context/DialogContext";
import { ArrowLeft, ChatCircle, FloppyDisk, Trash, WarningCircle } from "@phosphor-icons/react";

export default function IssueDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { can } = useCapabilities();
  const { confirm } = useDialog();
  const [i, setI] = useState(null);
  const [users, setUsers] = useState([]);
  const [comment, setComment] = useState("");
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [saving, setSaving] = useState(false);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: "", text: "" }), 4000); };

  const load = async () => {
    try {
      const [ir, ur] = await Promise.all([
        api.get(`/issues/${id}`),
        can("assign_issues") ? api.get("/users") : Promise.resolve({ data: { users: [] } }),
      ]);
      setI(ir.data);
      setUsers(ur.data.users || ur.data || []);
    } catch (e) { flash("error", formatAxiosError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!i) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

  const isOwner = i.raised_by === user.id;
  const isAssignee = i.assigned_to === user.id;
  const canEditBasics = can("manage_issues") || (isOwner && i.status === "open");
  const canAssign = can("assign_issues") || can("manage_issues");
  const canResolve = can("resolve_issues") || can("manage_issues") || isAssignee || isOwner;

  const patch = async (updates) => {
    setSaving(true);
    try {
      const r = await api.patch(`/issues/${id}`, updates);
      setI(r.data);
      flash("ok", "Saved.");
    } catch (e) { flash("error", formatAxiosError(e)); }
    finally { setSaving(false); }
  };

  const del = async () => {
    const ok = await confirm({
      title: "Delete this issue?",
      description: "The issue, along with any comments, will be permanently removed.",
      confirmText: "Delete issue",
      variant: "destructive",
    });
    if (!ok) return;
    try { await api.delete(`/issues/${id}`); nav("/issues"); }
    catch (e) { flash("error", formatAxiosError(e)); }
  };

  const postComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/issues/${id}/comments`, { text: comment.trim() });
      setComment("");
      await load();
    } catch (e) { flash("error", formatAxiosError(e)); }
  };

  return (
    <div className="p-8 lg:p-12 max-w-5xl">
      <button onClick={() => nav("/issues")} className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground">
        <ArrowLeft size={14} weight="bold" /> Back to Issues
      </button>
      <div className="mb-6">
        <div className="eyebrow mb-2 flex items-center gap-2"><WarningCircle size={12} weight="bold" /> Issue · {i.category || "General"}</div>
        <h1 className="font-display text-3xl font-black">{i.title}</h1>
      </div>
      {msg.text && (
        <div className={`mb-4 text-xs px-3 py-2 border ${msg.type === "ok" ? "border-green-500 bg-green-50 text-green-700" : "border-red-500 bg-red-50 text-red-700"}`}>{msg.text}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="border border-border p-5">
            <h3 className="font-semibold text-sm mb-2">Description</h3>
            {canEditBasics ? (
              <textarea className="input min-h-[220px] font-mono text-xs" defaultValue={i.description}
                onBlur={(e) => e.target.value !== i.description && patch({ description: e.target.value })} />
            ) : (
              <pre className="text-sm whitespace-pre-wrap font-sans">{i.description || "—"}</pre>
            )}
          </section>

          <section className="border border-border p-5">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><ChatCircle size={14} weight="bold" /> Comments ({(i.comments || []).length})</h3>
            <div className="space-y-3 mb-4">
              {(i.comments || []).length === 0 && <div className="text-xs text-muted-foreground">No comments yet.</div>}
              {(i.comments || []).map(c => (
                <div key={c.id} className="border-l-2 border-[#002FA7] pl-3 py-1">
                  <div className="text-xs text-muted-foreground mb-1">{new Date(c.created_at).toLocaleString()}</div>
                  <div className="text-sm whitespace-pre-wrap">{c.text}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input className="input flex-1" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment…" data-testid="issue-comment-input" />
              <button onClick={postComment} className="btn-primary text-sm" disabled={!comment.trim()} data-testid="issue-comment-submit">Post</button>
            </div>
          </section>

          {i.status === "resolved" || i.status === "closed" || can("resolve_issues") ? (
            <section className="border border-border p-5">
              <h3 className="font-semibold text-sm mb-2">Resolution notes</h3>
              {canResolve ? (
                <textarea className="input min-h-[100px]" defaultValue={i.resolution_notes || ""}
                  onBlur={(e) => e.target.value !== (i.resolution_notes || "") && patch({ resolution_notes: e.target.value })}
                  data-testid="resolution-notes" />
              ) : (
                <div className="text-sm whitespace-pre-wrap">{i.resolution_notes || "—"}</div>
              )}
            </section>
          ) : null}
        </div>

        <aside className="space-y-4">
          <div className="border border-border p-4">
            <div className="text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-2">Status</div>
            {(canResolve || can("manage_issues") || isOwner) ? (
              <select className="input" value={i.status} onChange={(e) => patch({ status: e.target.value })} disabled={saving} data-testid="issue-status-select">
                <option value="open">Open</option>
                <option value="in_progress">In progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            ) : <div className="font-semibold">{i.status}</div>}
          </div>
          <div className="border border-border p-4">
            <div className="text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-2">Priority</div>
            {canEditBasics ? (
              <select className="input" value={i.priority} onChange={(e) => patch({ priority: e.target.value })} disabled={saving}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            ) : <div className="font-semibold uppercase">{i.priority}</div>}
          </div>
          <div className="border border-border p-4">
            <div className="text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-2">Raised by</div>
            <div className="font-semibold">{i.raised_by_user?.name || "—"}</div>
            <div className="text-xs text-muted-foreground">{i.raised_by_user?.email}</div>
          </div>
          <div className="border border-border p-4">
            <div className="text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-2">Assigned to</div>
            {canAssign ? (
              <select className="input" value={i.assigned_to || ""} onChange={(e) => patch({ assigned_to: e.target.value })} disabled={saving} data-testid="issue-assign-select">
                <option value="">— Unassigned —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
              </select>
            ) : (
              <div>
                <div className="font-semibold">{i.assigned_to_user?.name || "— Unassigned —"}</div>
                <div className="text-xs text-muted-foreground">{i.assigned_to_user?.email}</div>
              </div>
            )}
          </div>
          <div className="border border-border p-4">
            <div className="text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-2">Timeline</div>
            <div className="text-xs text-muted-foreground">Created: {new Date(i.created_at).toLocaleString()}</div>
            {i.resolved_at && <div className="text-xs text-muted-foreground">Resolved: {new Date(i.resolved_at).toLocaleString()}</div>}
            <div className="text-xs text-muted-foreground">Updated: {new Date(i.updated_at).toLocaleString()}</div>
          </div>

          {(can("manage_issues") || (isOwner && i.status === "open")) && (
            <button onClick={del} className="w-full px-4 py-2 border border-red-500 text-red-600 hover:bg-red-50 text-sm flex items-center justify-center gap-2">
              <Trash size={14} weight="bold" /> Delete issue
            </button>
          )}
        </aside>
      </div>
    </div>
  );
}
